import {APIService} from '@custom-types/APIService';
import {APIServiceResponse} from '@custom-types/APIServiceResponse';
import {HttpRequest} from '@services/HttpClient';
import {APIResponseInterface} from '@services/services.type';
import {trackErrorEvents} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {getAPIUrl} from '@utils/service.utils';

type ResponseDataLeadMeta = APIServiceResponse<{
  countries: {
    city: string[];
    country_code: string;
    country_flag_code: string;
    country_name: string;
    sf_country: string;
  }[];
  interests: Record<'value' | 'id', string>[];
  preferredLanguages: Record<'value', string>[];
  cityOfResidence: Array<{value:string,label:string}>;
}>;

export type LeadMetaParsedData = {
  preferredLanguages: Array<Record<'label' | 'value', string>>;
  // countries: Array<Record<'label' | 'value' | 'flagCode', string>>;
  countryCode: Array<
    Record<
      'label' | 'value' | 'flagCode' | 'code' | 'name' | 'displayLabel',
      string
    >
  >;
  nationalities: Array<Record<'label' | 'value', string>>;
  cities: {[countryName: string]: Array<Record<'label' | 'value', string>>};
  cityOfResidence: ResponseDataLeadMeta['data']['cityOfResidence'];
};

export function getLeadMetaAPI(language?: string): APIService<LeadMetaParsedData> {
  const route = 'properties/feature/lead-meta';
  const url = getAPIUrl('property', route);
  const urlWithParams = language ? `${url}?language=${encodeURIComponent(language)}` : url;
  const httpRequest = new HttpRequest<ResponseDataLeadMeta>(urlWithParams);
  return {
    send: async () => {
      try {
        const response = await httpRequest.send();
        return parseLeadMeta(response.data);
      } catch (err: any) {
        trackErrorEvents({
          type: 'get_lead_generation_option_fetch_failed',
          errorStack: err,
        });
        throw err;
      }
    },
    abort: httpRequest.abort,
  };
}

function parseLeadMeta(data: ResponseDataLeadMeta['data']) {
  const preferredLanguages = data.preferredLanguages.map((item) => {
    return {
      label: item.value,
      value: item.value,
    };
  });
  const countries = data.countries.reduce(
    (acc: Record<string, string>[], item) => {
      if (acc.some(({value}) => value === item.sf_country)) return acc;
      return [
        ...acc,
        {
          label: item.country_name,
          value: item.sf_country,
        },
      ];
    },
    []
  );
  const countryCode = Array.from(
    new Set(
      data.countries.map((item) => ({
        label: `${item.country_name} (${item.country_code})`,
        value: getValueStrFromCountyCodeAndValue(
          item.country_code,
          item.sf_country
        ),
        flagCode: item.country_flag_code,
        code: item.country_code,
        name: item.sf_country,
        displayLabel: item.country_code ?? '___',
      }))
    )
  );

  const cities = Object.fromEntries(
    data.countries.map((item) => [
      item.country_name,
      Array.from(new Set(item.city)).map((city) => ({
        label: city,
        value: city,
      })),
    ])
  );
  return {
    preferredLanguages,
    // countries,
    countryCode,
    nationalities: countries,
    cities,
    cityOfResidence: data.cityOfResidence
  };
}

const COUNTY_CODE_DELIMITER = '___';
export function getValueStrFromCountyCodeAndValue(code: string, name: string) {
  return `${code}${COUNTY_CODE_DELIMITER}${name}`;
}
export function getCountryCodeAndNameFromValue(value: string) {
  const [code, name] = value.split(COUNTY_CODE_DELIMITER);
  return {code, name};
}
